import {
  useCallback, useContext, useEffect, useState
} from "react";
import { useDispatch, useSelector } from "react-redux";

import { updateProjectItem } from "Utils/Item";
import { duplicateCheckActiveProjectItemIdSelect, duplicateCheckCacheSet } from "Store/Actions";
import { selectDuplicateCheckCache } from "Store/Selectors/Omikron";
import { useProjectItem } from "../../Hooks/UseProjectItem";
import { IUseMergedItem } from "./IUseMergedItem";
import { useResult } from "./UseResult";
import { ProjectItemContext } from "../../ProjectItemContext/ProjectItemContext";

export function useMergedItem(): IUseMergedItem {
  const dispatch = useDispatch();
  const { projectId, projectItemId, selectedResultItemId: resultItemId } = useContext(ProjectItemContext);
  const { project, projectItem, nextProjectItemId } = useProjectItem(projectId, projectItemId);
  const cache = useSelector(selectDuplicateCheckCache);
  const [mergedProjectItem, setMergedProjectItem] = useState(projectItem);
  const [mergeSelectionMap, setMergeSelectionMap] = useState(new Map<string, "source" | "target">());
  const [isMergeSaved, setIsMergeSaved] = useState(false);
  const result = useResult(resultItemId);

  useEffect(() => {
    setMergedProjectItem(projectItem);
    setMergeSelectionMap(new Map<string, "source" | "target">());
    setIsMergeSaved(false);
  }, [projectItem, resultItemId]);

  const onMergeChange = useCallback(
    (key: string, value: unknown) => {
      const [sourceOrTarget, fieldName] = key.split(`.`);
      if (mergeSelectionMap.get(fieldName) === sourceOrTarget) {
        return;
      }

      setMergeSelectionMap((previous) => new Map(previous.set(fieldName, sourceOrTarget as "source" | "target")));
      setMergedProjectItem((previous) => ({
        ...previous,
        [fieldName]: value ?? null
      }));
    },
    [mergeSelectionMap]
  );

  const onMergeSave = useCallback(
    (pItemId: string) => {
      if (mergedProjectItem == null) {
        return;
      }

      const updatedProject = updateProjectItem(cache ?? project, pItemId, () => ({
        ...mergedProjectItem,
        "@isDC": true,
        "@targetId": result[`@targetId`]
      }));
      dispatch(duplicateCheckCacheSet(updatedProject));
      dispatch(duplicateCheckActiveProjectItemIdSelect(nextProjectItemId));
      setIsMergeSaved(true);
    },
    [cache, dispatch, mergedProjectItem, nextProjectItemId, project, result]
  );

  return {
    mergedProjectItem,
    mergeSelectionMap,
    onMergeChange,
    onMergeSave,
    isMergeSaved
  };
}
